import getConfig from 'next/config';
import { DefaultPrivacyLevel, RumInitConfiguration } from '@datadog/browser-rum';
import { TrackingConsent } from '@datadog/browser-core';

const { publicRuntimeConfig } = getConfig();

/**
 * Returns the configured sample rate if its is between 0 and 100 otherwise,
 * null is returned.
 */
const convertValidSampleRate = (sampleRate?: string): number | undefined => {
  if (sampleRate) {
    const sampleRateNumber = parseFloat(sampleRate);
    if (sampleRateNumber >= 0 && sampleRateNumber <= 100) {
      return sampleRateNumber;
    }
  }
  return undefined;
};

/**
 * Datadog configuration options to initialize RUM
 * @param trackingConsent set to true for configuration that allows for DD to track user interactions; false otherwise
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/#configuration
 */
export const getDatadogConfig = (trackingConsent: boolean): RumInitConfiguration => ({
  applicationId: publicRuntimeConfig.DD_APP_ID,
  clientToken: publicRuntimeConfig.DD_CLIENT_TOKEN,
  service: publicRuntimeConfig.DD_SERVICE,
  env: publicRuntimeConfig.DD_ENV,
  version: publicRuntimeConfig.DD_VERSION,
  sessionSampleRate: convertValidSampleRate(publicRuntimeConfig.DD_SESSION_SAMPLE_RATE) || 100,
  sessionReplaySampleRate: convertValidSampleRate(publicRuntimeConfig.DD_SESSION_REPLAY_SAMPLE_RATE) || 5,
  traceSampleRate: convertValidSampleRate(publicRuntimeConfig.DD_TRACE_SAMPLE_RATE) || 5,
  trackViewsManually: false,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  allowedTracingUrls: [window.location.origin],
  defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
  trackSessionAcrossSubdomains: true,
  useSecureSessionCookie: true,
  // The useCrossSiteSessionCookie is deprecated in favor of usePartitionedCrossSiteSessionCookie but for now we are
  // using the deprecated one because using usePartitionedCrossSiteSessionCookie does not work as expected in Chrome.
  // Created a support ticket with Datadog to investigate the issue
  // https://help.datadoghq.com/hc/en-us/requests/1737215
  useCrossSiteSessionCookie: true,
  trackingConsent: trackingConsent ? TrackingConsent.GRANTED : TrackingConsent.NOT_GRANTED,
  excludedActivityUrls: [
    // Heartbeat +
    /cvent.pubnubapi.com/,
    /stream-io-api.com/,
    // Analytics
    /api-js.mixpanel.com/,
    /batch_facts/,
    /analytics/,
    // Video
    /video.app.cvent.com/,
    /video-edge/,
    /live-video.net/,
    /video-sync-upload-bucket/,
    /ingest.chime.aws/,
    /brightcove/,
    /bcove/,
    /bcovlive/,
    /api.brightcove/,
    /api.bcovlive/,
    // Others
    /salesforceliveagent.com/
  ]
});
